import { Controller } from "@hotwired/stimulus"
import { escapeHtml as escapeHtmlValue } from "../helpers/escape_html"

const PREDEFINED_PERSONAS = [
  "Accountant",
  "Business Analyst",
  "C-level Executive",
  "Compliance Officer",
  "Consultant",
  "Customer Support",
  "Data Analyst",
  "Designer",
  "Educator",
  "Engineer",
  "Finance Manager",
  "HR Manager",
  "IT Manager",
  "Legal Counsel",
  "Marketing Manager",
  "Operations Manager",
  "Product Manager",
  "Project Manager",
  "Researcher",
  "Sales Manager"
]

export default class extends Controller {
  static targets = ["input", "suggestions"]
  static values = {
    activityId: String,
    url: String
  }

  connect() {
    this.selectedIndex = -1
    // Focus input when modal opens
    setTimeout(() => {
      if (this.hasInputTarget) {
        this.inputTarget.focus()
      }
    }, 100)
  }

  handleKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      if (this.selectedIndex >= 0 && this.hasSuggestionsTarget) {
        const items = this.suggestionsTarget.querySelectorAll("[data-suggestion]")
        if (items[this.selectedIndex]) {
          this.selectSuggestion(items[this.selectedIndex].dataset.suggestion)
          return
        }
      }
      this.save()
    } else if (event.key === "ArrowDown") {
      event.preventDefault()
      this.navigateSuggestions(1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.navigateSuggestions(-1)
    } else if (event.key === "Escape") {
      this.hideSuggestions()
    }
  }

  onInput() {
    const query = this.inputTarget.value.trim()
    if (query.length === 0) {
      this.hideSuggestions()
      return
    }

    // Get the text after the last comma for filtering
    const parts = query.split(",")
    const currentQuery = parts[parts.length - 1].trim()
    if (currentQuery.length === 0) {
      this.hideSuggestions()
      return
    }

    this.showFilteredSuggestions(currentQuery)
  }

  showFilteredSuggestions(query) {
    if (!this.hasSuggestionsTarget) return

    const filtered = PREDEFINED_PERSONAS.filter(p =>
      p.toLowerCase().includes(query.toLowerCase())
    )

    if (filtered.length === 0) {
      this.hideSuggestions()
      return
    }

    this.selectedIndex = -1
    this.suggestionsTarget.innerHTML = filtered.map(persona =>
      `<div class="px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100 rounded"
            data-suggestion="${this.escapeHtml(persona)}"
            data-action="mousedown->add-persona#onSuggestionClick">
        ${this.escapeHtml(persona)}
      </div>`
    ).join("")

    this.suggestionsTarget.classList.remove("hidden")
  }

  hideSuggestions() {
    if (this.hasSuggestionsTarget) {
      this.suggestionsTarget.classList.add("hidden")
      this.selectedIndex = -1
    }
  }

  navigateSuggestions(direction) {
    if (!this.hasSuggestionsTarget) return
    const items = this.suggestionsTarget.querySelectorAll("[data-suggestion]")
    if (items.length === 0) return

    if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
      items[this.selectedIndex].classList.remove("bg-gray-100")
    }

    this.selectedIndex += direction
    if (this.selectedIndex < 0) this.selectedIndex = items.length - 1
    if (this.selectedIndex >= items.length) this.selectedIndex = 0

    items[this.selectedIndex].classList.add("bg-gray-100")
    items[this.selectedIndex].scrollIntoView({ block: "nearest" })
  }

  onSuggestionClick(event) {
    event.preventDefault()
    const persona = event.currentTarget.dataset.suggestion
    this.selectSuggestion(persona)
  }

  selectSuggestion(persona) {
    this.inputTarget.value = persona
    this.inputTarget.focus()
    this.hideSuggestions()
  }

  save() {
    const rawValue = this.inputTarget.value.trim()

    if (!rawValue) {
      if (window.toastController) {
        window.toastController.error("Please enter a persona name")
      }
      return
    }

    // Split by commas to support multiple personas
    const personaNames = rawValue.split(",").map(v => v.trim()).filter(v => v !== "")

    if (personaNames.length === 0) {
      if (window.toastController) {
        window.toastController.error("Please enter a persona name")
      }
      return
    }

    // Get the guideline editor controller
    const guidelineEditor = document.querySelector("[data-controller~='guideline-editor']")
    if (!guidelineEditor) {
      console.error("Could not find guideline-editor controller")
      return
    }

    const editorController = this.application.getControllerForElementAndIdentifier(
      guidelineEditor,
      "guideline-editor"
    )

    // Add each persona
    personaNames.forEach(personaName => {
      if (editorController && typeof editorController.addPersonaToList === "function") {
        editorController.addPersonaToList(personaName)
        this.saveToBackend(personaName)
      } else {
        console.error("addPersonaToList method not found on controller")
      }
    })

    // Close modal
    const modal = this.element.closest("[data-controller~='modal']")
    if (modal) {
      const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
      if (modalController) {
        modalController.close()
      }
    }

    // Clear input
    this.inputTarget.value = ""
    this.hideSuggestions()
  }

  saveToBackend(personaName) {
    fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
      },
      body: JSON.stringify({ persona_name: personaName })
    })
      .then(response => response.json())
      .then(data => {
        if (data.status === "success") {
          this.addPersonaToRegenerateForm(data.persona)

          if (window.toastController) {
            window.toastController.success(`Persona "${data.persona}" added successfully`)
          }
        } else if (data.error) {
          if (window.toastController) {
            window.toastController.error(data.error)
          }
        }
      })
      .catch(error => {
        if (window.toastController) {
          window.toastController.error("Failed to save persona")
        }
      })
  }

  addPersonaToRegenerateForm(personaName) {
    const personaTagsContainer = document.querySelector('[data-guideline-generator-target="personaTags"]')

    if (personaTagsContainer) {
      const existingTags = personaTagsContainer.querySelectorAll("[data-tag-value]")
      const existingValues = Array.from(existingTags).map(t => t.dataset.tagValue.toLowerCase())

      if (existingValues.includes(personaName.toLowerCase())) {
        return
      }

      const tagHTML = `
        <span class="inline-flex items-center gap-1 px-2 py-1 bg-[#F2F3F5] rounded-full text-xs font-medium text-[#374151]"
              data-tag-value="${this.escapeHtml(personaName)}">
          ${this.escapeHtml(personaName)}
          <button type="button" class="hover:text-[#B42318]" data-action="click->tag-input-guideline#removeTag">
            <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 6L6 18M6 6L18 18"/>
            </svg>
          </button>
        </span>
      `

      personaTagsContainer.insertAdjacentHTML("beforeend", tagHTML)
    }
  }

  escapeHtml(text) {
    return escapeHtmlValue(text)
  }
}
